// "How the world learns" — a neutral SVG world map with one bubble per broad
// region, sized by activity and clickable to scope the global stats.
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { useTranslation } from "react-i18next";
import { cx } from "@/components/ui";
import type { Region } from "@/api/stats-types";
import { REGION_LIST } from "./regions";

const GEO_URL = "/world-110m.json";

export function WorldMap({
  values,
  selected,
  onSelect,
}: {
  values: Partial<Record<Region, number>>;
  selected: Region | null;
  onSelect: (region: Region | null) => void;
}) {
  const { t } = useTranslation();
  const max = Math.max(1, ...REGION_LIST.map((r) => values[r.region] ?? 0));

  return (
    <div className="card overflow-hidden p-2">
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: 155 }}
        width={820}
        height={380}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#eef1f5"
                stroke="#ffffff"
                strokeWidth={0.5}
                style={{
                  default: { outline: "none" },
                  hover: { outline: "none", fill: "#e6eaf0" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {REGION_LIST.map((r) => {
          const v = values[r.region] ?? 0;
          const radius = 7 + 20 * Math.sqrt(v / max);
          const isSel = selected === r.region;
          const dim = selected != null && !isSel;
          return (
            <Marker
              key={r.region}
              coordinates={r.center}
              onClick={() => onSelect(isSel ? null : r.region)}
              style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" }, pressed: {} }}
            >
              <circle
                r={radius}
                fill={r.color}
                fillOpacity={dim ? 0.25 : 0.55}
                stroke={r.color}
                strokeWidth={isSel ? 3 : 1.5}
                strokeOpacity={dim ? 0.4 : 1}
              />
              {v > 0 && (
                <text
                  textAnchor="middle"
                  y={4}
                  style={{ fontFamily: "inherit", fontSize: 11, fontWeight: 700, fill: "#fff", pointerEvents: "none" }}
                >
                  {v}
                </text>
              )}
            </Marker>
          );
        })}
      </ComposableMap>

      <div className="flex flex-wrap gap-1.5 px-1 pb-1">
        {REGION_LIST.map((r) => {
          const isSel = selected === r.region;
          return (
            <button
              key={r.region}
              type="button"
              onClick={() => onSelect(isSel ? null : r.region)}
              className={cx(
                "rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition",
                isSel ? "text-white ring-transparent" : "bg-white text-ink-soft ring-black/10 hover:bg-black/[0.03]",
              )}
              style={isSel ? { backgroundColor: r.color } : undefined}
            >
              <span
                className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                style={{ backgroundColor: r.color }}
              />
              {t(r.short)}
              {values[r.region] != null && <span className="ml-1 opacity-70">{values[r.region]}</span>}
            </button>
          );
        })}
        {selected && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="rounded-full px-2.5 py-1 text-xs font-medium text-ink-faint underline-offset-2 hover:underline"
          >
            {t("Clear")}
          </button>
        )}
      </div>
    </div>
  );
}
